import * as React from "react";

import { XaviorPulseLoader } from "@/common/components/XaviorPulseLoader";

import { markSsoAutoConnectAttempted } from "../lib/sso-autoconnect";

export interface SsoAutoConnectProps {
  /**
   * Fire the provider data-scope grant. App wires this to the one `connectProvider`
   * flow with the SSO provider + signed-in email as `login_hint` (so the provider
   * targets that account and skips its chooser). Resolves only on failure — the
   * success path is a same-window redirect that unloads the page.
   */
  onAutoConnect: () => Promise<void> | void;
  /**
   * Called if the grant could not be STARTED (e.g. the authorize-URL fetch
   * failed, so no redirect happened) so the parent can fall back to the manual
   * ConnectPage chooser this session.
   */
  onError: () => void;
}

/**
 * SSO auto-redirect to the data-scope grant (PDR 011 / PDR 010 D3).
 *
 * An SSO identity at the zero-workspace connect leaf already has a provider, so
 * we skip the ConnectPage chooser (that screen exists for magic-link, which has
 * no provider) and fire the grant straight away. A one-shot sessionStorage flag
 * is marked BEFORE redirecting so a denial that lands us back here does not
 * re-fire — the parent renders the chooser as the fallback once
 * `ssoAutoConnectAttempted()` is true. This loader is only ever visible for the
 * brief pre-redirect window, or if starting the grant fails (→ `onError`).
 */
export function SsoAutoConnect({ onAutoConnect, onError }: SsoAutoConnectProps) {
  const firedRef = React.useRef(false);
  React.useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    markSsoAutoConnectAttempted();
    void (async () => {
      try {
        await onAutoConnect();
      } catch {
        onError();
      }
    })();
  }, [onAutoConnect, onError]);

  return <XaviorPulseLoader label="Setting up your workspace…" />;
}
